import { Suspense } from "react"
import { Routes, Route, Navigate, useLocation } from "react-router-dom"
import { useSelector } from "react-redux"
import LoginPage from "../components/login/LoginPage"
import Layout from "../components/Layout"
import ProtectedRoute from "./ProtectedRoute"
import PropertyRequiredGuard from "./PropertyRequiredGuard"
import ExecutiveDashboardGuard from "./ExecutiveDashboardGuard"
import SignupForm from "../components/signup/SignupForm"
import InviteUser from "../components/invite-user/InviteUser"
import ResetPassword from "../components/login/ResetPassword"
import ApprovalPage from "../components/postApproved/ApprovalPage.jsx"
import SohoLoader from "../components/common/SohoLoader/SohoLoader"
import Dashboard from "../components/dashboard/Dashboard"
import {
  Dashboard2,
  Calendar,
  AICommunityInbox,
  AIPlanner,
  SettingsPage,
  SocialNetwork,
  MediaLibrary,
  AIAssetManager,
  AIAssetManagerDetails,
  AIAssetManagerCategoryPage,
  SettingUp,
  OrganizationSettings,
  BillingInvoices,
  AIAuditsPage,
  UGC,
} from "./lazyPages"
import {
  isSettingUpOnboardingSession,
  markSettingUpFromOnboarding,
  shouldRedirectSocialOAuthToSettingUp,
} from "../lib/settingUpFlow"

function SettingUpGuard() {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const oauthReturn = Boolean(params.get("token") || params.get("code"))
  if (oauthReturn) {
    markSettingUpFromOnboarding()
  }
  const resumeStep =
    typeof location.state?.resumeStep === "number" &&
    location.state.resumeStep >= 1 &&
    location.state.resumeStep <= 3
  const settingUpStep =
    typeof location.state?.settingUpStep === "number" &&
    location.state.settingUpStep >= 1 &&
    location.state.settingUpStep <= 3
  const fromOnboarding =
    location.state?.onboardingData != null ||
    resumeStep ||
    settingUpStep ||
    oauthReturn ||
    isSettingUpOnboardingSession()
  if (!fromOnboarding) {
    return <Navigate to="/calendar" replace />
  }
  return (
    <Suspense fallback={<SohoLoader size="lg" fullScreen />}>
      <SettingUp />
    </Suspense>
  )
}

/** Keep onboarding OAuth off the Settings shell (sidebar + "Loading page…"). */
function SocialNetworkEntry() {
  const location = useLocation()
  if (shouldRedirectSocialOAuthToSettingUp(location.search)) {
    markSettingUpFromOnboarding()
    return <Navigate to={`/settingup${location.search}`} replace />
  }
  return (
    <ProtectedRoute>
      <PropertyRequiredGuard>
        <Layout />
      </PropertyRequiredGuard>
    </ProtectedRoute>
  )
}

const AppRoutes = () => {
  const isAuthenticated = useSelector((state) => !!state.auth?.token)

  return (
    <Routes>
      <Route
        path="/"
        element={isAuthenticated ? <Navigate to="/calendar" replace /> : <Navigate to="/login" replace />}
      />

      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupForm />} />
      <Route path="settingup" element={<SettingUpGuard />} />
      <Route path="settings/social-network" element={<SocialNetworkEntry />}>
        <Route
          index
          element={
            <Suspense fallback={<SohoLoader size="lg" fullScreen />}>
              <SocialNetwork />
            </Suspense>
          }
        />
      </Route>
      <Route path="invite-user/:token?" element={<InviteUser />} />
      <Route path="/reset-password/:token?" element={<ResetPassword />} />
      <Route path="/post-approved/:token?" element={<ApprovalPage />} />

      <Route
        element={
          <ProtectedRoute>
            <PropertyRequiredGuard>
              <Layout />
            </PropertyRequiredGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/calendar" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route
          path="multi-property-dashboard"
          element={
            <ExecutiveDashboardGuard>
              <Dashboard2 />
            </ExecutiveDashboardGuard>
          }
        />
        <Route path="ai-community-inbox" element={<AICommunityInbox />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="ai-planner" element={<AIPlanner />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="medialibrary" element={<MediaLibrary />} />
        <Route path="media-library-details" element={<Navigate to="/medialibrary" replace />} />
        <Route path="media-library/category/:categorySlug" element={<Navigate to="/medialibrary" replace />} />
        <Route path="aiassetmanager" element={<AIAssetManager />} />
        <Route path="ai-asset-manager-details" element={<AIAssetManagerDetails />} />
        <Route path="ai-asset-manager/category/:categorySlug" element={<AIAssetManagerCategoryPage />} />
        <Route path="ai-audits" element={<AIAuditsPage />} />
        <Route path="ugc" element={<UGC />} />
        <Route path="organization-settings" element={<OrganizationSettings />} />
        <Route path="organization-settings/billing" element={<BillingInvoices />} />
      </Route>

      <Route
        path="*"
        element={isAuthenticated ? <Navigate to="/calendar" replace /> : <Navigate to="/login" replace />}
      />
    </Routes>
  )
}

export default AppRoutes
